const FORBIDDEN_PATH_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

function isRecord(value: unknown): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!isRecord(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

export function deepClone<T>(value: T): T {
    return value === undefined ? value : JSON.parse(JSON.stringify(value));
}

export function deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;

    if (Array.isArray(a) || Array.isArray(b)) {
        return Array.isArray(a)
            && Array.isArray(b)
            && a.length === b.length
            && a.every((value, index) => deepEqual(value, b[index]));
    }

    if (!isRecord(a) || !isRecord(b)) {
        return false;
    }

    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();

    return aKeys.length === bKeys.length
        && aKeys.every((key, index) => key === bKeys[index] && deepEqual(a[key], b[key]));
}

export function normalizeLoanPath(rawPath: string): string {
    if (typeof rawPath !== 'string') {
        throw new Error('Path must be a string');
    }
    const trimmed = rawPath.trim();
    if (!trimmed) {
        throw new Error('Path must not be empty');
    }

    const sanitized = trimmed
        .replace(/^\.+/, '')
        .replace(/\.+$/, '')
        .replace(/\[(\d+)\]/g, '.$1');

    if (!sanitized) {
        throw new Error('Path must not be empty');
    }

    const segments = sanitized.split('.').filter(Boolean);
    if (segments.length === 0) {
        throw new Error('Path must not be empty');
    }

    for (const segment of segments) {
        if (!segment.match(/^[A-Za-z0-9_-]+$/)) {
            throw new Error(`Invalid path segment: ${segment}`);
        }
        if (FORBIDDEN_PATH_SEGMENTS.has(segment)) {
            throw new Error(`Forbidden path segment: ${segment}`);
        }
    }

    return segments.join('.');
}

function splitLoanPath(path: string): string[] {
    return normalizeLoanPath(path).split('.');
}

function isArrayIndexSegment(segment: string): boolean {
    return /^\d+$/.test(segment);
}

export function getPathSnapshot(target: unknown, path: string): { exists: boolean; value: unknown } {
    if (!path) {
        return {
            exists: target !== undefined,
            value: target,
        };
    }

    const segments = splitLoanPath(path);
    let cursor: any = target;

    for (const segment of segments) {
        if (!isRecord(cursor) && !Array.isArray(cursor)) {
            return { exists: false, value: undefined };
        }
        if (!Object.prototype.hasOwnProperty.call(cursor, segment)) {
            return { exists: false, value: undefined };
        }
        cursor = cursor[segment];
    }

    return { exists: true, value: cursor };
}

export function setPathValue(target: Record<string, any>, path: string, value: unknown): void {
    const segments = splitLoanPath(path);
    let cursor: any = target;

    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        const nextSegment = segments[i + 1];
        const expectsArray = isArrayIndexSegment(nextSegment);
        const next = cursor?.[segment];
        const nextIsContainer = isRecord(next) || Array.isArray(next);

        if (!nextIsContainer) {
            cursor[segment] = expectsArray ? [] : {};
        }
        cursor = cursor[segment];
    }

    const lastSegment = segments[segments.length - 1];
    cursor[lastSegment] = deepClone(value);
}

export function unsetPathValue(target: Record<string, any>, path: string): void {
    const segments = splitLoanPath(path);
    const stack: any[] = [target];
    let cursor: any = target;

    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        if ((!isRecord(cursor) && !Array.isArray(cursor)) || !Object.prototype.hasOwnProperty.call(cursor, segment)) {
            return;
        }
        const next = cursor[segment];
        if (!isRecord(next) && !Array.isArray(next)) {
            return;
        }
        cursor = next;
        stack.push(cursor);
    }

    const leaf = segments[segments.length - 1];
    if (!Object.prototype.hasOwnProperty.call(cursor, leaf)) {
        return;
    }
    delete cursor[leaf];

    for (let i = stack.length - 1; i >= 1; i -= 1) {
        const node = stack[i];
        const parent = stack[i - 1];
        const segment = segments[i - 1];
        if (isPlainObject(node) && Object.keys(node).length === 0 && isRecord(parent)) {
            delete (parent as Record<string, any>)[segment];
        }
    }
}
